import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Timeline,
  Tag,
  Typography,
  Spin,
  Empty,
  Space,
  Badge,
  Divider,
  Pagination,
} from "antd";
import {
  UserOutlined,
  ClockCircleOutlined,
  EditOutlined,
  PlusCircleOutlined,
  RightOutlined,
  DownOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { ACTION_COLORS } from "../../variables/rbaColors";
import { profilsService } from "../../services/ProfileService";
import CBModal from "../../components/CBModal";

import {
  cleHabilitation, libelleLisible, diffHabilitations, numeroVersion,
  precedenteDansPage, marquerNouveau, groupesHistorique,
} from "../../utils/diffHabilitations";

const { Text } = Typography;

// Icône selon le type d'opération déduit
const operationIcon = (version) => {
  if (version === 1) return <PlusCircleOutlined style={{ color: "#1a6b3c" }} />;
  return <EditOutlined style={{ color: "#fa8c16" }} />;
};

const operationLabel = (version) => {
  if (version === 1) return { color: "green", label: i18n.t("profils:historique.creation") };
  /* Un numero non calculable - total incoherent - se dit, plutot que d'afficher
     « Version null » et de laisser croire a une donnee corrompue. */
  if (!Number.isFinite(version)) return { color: "default", label: i18n.t("profils:historique.versionInconnue") };
  return { color: "orange", label: i18n.t("profils:historique.version", { version }) };
};

/* Historique des versions d'un profil et de ses habilitations, dans une fenêtre
   centrée comme le reste de l'application (CBModal) : un tiroir latéral de 520 px
   écrasait la liste des habilitations de chaque version. */
/**
 * Une pastille d'habilitation, portant le LIBELLE.
 *
 * Le code n'est plus consultable depuis l'interface ; il n'a donc rien a faire
 * ici non plus. `marque` prefixe « + » ou « − » : la couleur seule ne dit rien a
 * qui la distingue mal, et un historique se lit souvent imprime.
 */
/**
 * Le point « N » du coin, pour les habilitations qui viennent d'arriver.
 *
 * AU COIN ET NON DANS LE TEXTE. Le prefixe « + » vit a l'interieur de la pastille,
 * au milieu d'un libelle qui fait parfois quarante caracteres : l'oeil qui parcourt
 * un groupe de trente pastilles ne le voit pas. Un point en saillie se repere sans
 * etre lu.
 *
 * `pointerEvents: none` : il deborde sur la pastille voisine de deux pixels, et sans
 * cela il volerait son survol.
 */
const PointNouveau = () => (
  <span
    title={i18n.t("profils:historique.nouvelleHabilitation")}
    aria-label={i18n.t("profils:historique.nouvelleHabilitationAria")}
    style={{
      position: "absolute",
      top: -5,
      right: -5,
      width: 14,
      height: 14,
      borderRadius: "50%",
      background: "#1a6b3c",
      color: "#ffffff",
      fontSize: 9,
      fontWeight: 700,
      lineHeight: "14px",
      textAlign: "center",
      border: "1.5px solid #ffffff",
      pointerEvents: "none",
    }}
  >
    N
  </span>
);

const Pastille = ({ hab, marque, barre, nouveau }) => {
  const pastille = (
    <Tag
      color={ACTION_COLORS[hab.action]}
      style={{
        fontSize: 11,
        margin: 0,
        textDecoration: barre ? "line-through" : undefined,
        opacity: barre ? 0.75 : 1,
      }}
    >
      {marque ? marque + " " : ""}{libelleLisible(hab)}
    </Tag>
  );

  /* L'enveloppe n'est posee QUE si le point l'exige : un `position: relative`
     inutile sur chaque pastille des groupes « Retirees » et « Inchangees » ne
     servirait a rien et changerait leur empilement. */
  if (!nouveau) return pastille;

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      {pastille}
      <PointNouveau />
    </span>
  );
};

/** Un groupe de pastilles sous son intitule, ou rien si le groupe est vide. */
const Groupe = ({ titre, habs, marque, barre, couleurTitre, nouveau }) =>
(habs.length === 0 ? null : (
  <div style={{ marginTop: 8 }}>
    {/* Un groupe sans intitule - le repliable - ne doit pas reserver la place
        d'un intitule vide. */}
    {titre ? (
      <Text
        style={{ fontSize: 11, display: "block", marginBottom: 6, color: couleurTitre }}
      >
        {titre}
      </Text>
    ) : null}
    {/* L'ecart s'ouvre pour laisser la place au point du coin, et seulement dans
          le groupe qui en porte : ailleurs, il eloignerait les pastilles sans
          raison. */}
    <Space size={nouveau ? [10, 10] : [4, 4]} wrap>
      {habs.map((hab) => (
        <Pastille
          key={cleHabilitation(hab)}
          hab={hab}
          marque={marque}
          barre={barre}
          nouveau={nouveau}
        />
      ))}
    </Space>
  </div>
));

export default function HistoriqueProfilModal({ open, onClose, profil }) {
  const { t } = useTranslation("profils");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  /* Les blocs deployes, par indice d'entree. REPLIES PAR DEFAUT : c'est tout l'objet
     du changement, ne pas imposer cent-soixante-dix-neuf pastilles pour en lire une. */
  const [deployes, setDeployes] = useState(() => new Set());
  const basculer = (idx) => setDeployes((avant) => {
    const apres = new Set(avant);
    if (apres.has(idx)) apres.delete(idx); else apres.add(idx);
    return apres;
  });

  /* LA DATE DE LA DERNIERE MISE A JOUR, prise sur la PREMIERE PAGE seulement. La
     liste est triee du plus recent au plus ancien : `items[0]` n'est la derniere
     version que page 0. Sur les pages suivantes, ce serait la plus recente de la
     page, et l'en-tete annoncerait une date qui recule a mesure qu'on remonte. */
  const [derniereMaj, setDerniereMaj] = useState(null);

  const load = useCallback(
    async (p = 0) => {
      if (!profil) return;
      setLoading(true);
      try {
        const res = await profilsService.history(
          profil.profilTrackingId,
          p,
          pageSize,
        );
        const data = res.data?.data || res.data || {};
        // Supporte pagination Spring (content + totalElements) ou liste plate
        const liste = Array.isArray(data) ? data : (data.content || []);
        setItems(liste);
        setTotal(Array.isArray(data) ? data.length : (data.totalElements || 0));
        setDeployes(new Set());
        if (p === 0) {
          const derniere = liste[0];
          setDerniereMaj(derniere ? (derniere.createdAt || null) : null);
        }
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [profil],
  );

  useEffect(() => {
    if (open) {
      setPage(0);
      load(0);
    }
  }, [open, load]);

  const handlePageChange = (p) => {
    const zero = p - 1;
    setPage(zero);
    load(zero);
  };

  // ── Rendu d'un item d'historique ──────────────────────────────────────────
  const renderItem = (h, idx) => {
    /* LE NUMERO SE CALCULE, il n'est pas envoye. `ProfilResponse` ne porte pas de
       champ `version` : l'ancien `h.version ?? idx + 1` numerotait donc a l'envers,
       la requete serveur triant du plus recent au plus ancien. La derniere
       modification recevait le numero 1, se comparait a un ensemble vide, et
       s'annoncait comme une creation avec toutes les habilitations en ajout. */
    const version = numeroVersion(total, page, pageSize, idx);
    const op = operationLabel(version);

    /* LA CREATION se compare a un ensemble VIDE : tout y est un ajout, et c'est
       exact. Les autres se comparent a l'entree SUIVANTE, la liste etant
       decroissante. Quand la page s'arrete avant, le diff se declare non
       comparable et on affiche la liste sans rien annoncer - plutot que de
       presenter deux cents habilitations comme deux cents ajouts. */
    const precedente = version === 1
      ? { habilitations: [] }
      : precedenteDansPage(items, idx);
    const d = diffHabilitations(precedente ? (precedente.habilitations || []) : null, h.habilitations);
    const g = groupesHistorique(d, version);
    return {
      dot: operationIcon(version),
      children: (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 4,
          }}
        >
          {/* En-tête ligne */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 4,
              marginBottom: 8,
            }}
          >
            <Space size={6}>
              <Tag color={op.color}>{op.label}</Tag>
              {h.createdBy ? (
                <Space size={4}>
                  <UserOutlined style={{ color: "#9ca3af", fontSize: 12 }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {h.createdBy}
                  </Text>
                </Space>
              ) : null}
            </Space>
            <Space size={4}>
              <ClockCircleOutlined style={{ color: "#9ca3af", fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {/* `createdAt` ET NON `updatedAt || createdAt`. Chaque version est une
                    ligne distincte : `createdAt` dit quand elle a ete faite,
                    `updatedAt` quand elle a ete REMPLACEE par la suivante. L'ancien
                    repli datait donc chaque version de sa propre fin - et deux entrees
                    consecutives portaient le meme horodatage, celui du passage de l'une
                    a l'autre. Seule la version courante, dont `updatedAt` est nul,
                    s'affichait juste. */}
                {h.createdAt ? dayjs(h.createdAt).format("DD/MM/YYYY HH:mm") : "-"}
              </Text>
            </Space>
          </div>

          {/* Nom du profil à ce moment */}
          <Text strong style={{ fontSize: 13 }}>
            {h.profilNom || profil?.profilNom}
          </Text>

          {/* Habilitations de cette version, et ce qui a change */}
          {(h.habilitations?.length > 0 || d.retirees.length > 0) && (
            <>
              <Divider style={{ margin: "8px 0" }} />
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: "block" }}
                >
                  {t("commun.nbHabilitations", { count: h.habilitations?.length || 0 })}
                  {d.comparable && (d.ajoutees.length > 0 || d.retirees.length > 0) ? (
                    <>
                      {" · "}
                      {d.ajoutees.length > 0 ? `+${d.ajoutees.length}` : ""}
                      {d.ajoutees.length > 0 && d.retirees.length > 0 ? " " : ""}
                      {d.retirees.length > 0 ? `−${d.retirees.length}` : ""}
                    </>
                  ) : null}
                </Text>

                {/* Hors de la page, la version precedente est inconnue : on le DIT,
                    au lieu de laisser croire que rien n'a change. */}
                {!d.comparable && (
                  <Text type="secondary" style={{ fontSize: 10, display: "block", marginTop: 4 }}>
                    {t("historique.precedenteHorsPage")}
                  </Text>
                )}

                <Groupe
                  titre={t("historique.ajoutees")}
                  habs={g.ajoutees}
                  marque="+"
                  couleurTitre="#1a6b3c"
                  nouveau={marquerNouveau(version, d.comparable)}
                />
                <Groupe
                  titre={t("historique.retirees")}
                  habs={g.retirees}
                  marque="−"
                  barre
                  couleurTitre="#b91c1c"
                />

                {/* CE QUI N'A PAS CHANGE EST COMPTE, PAS DEPLIE. Chaque version
                    reportait la liste entiere : dix versions de cent-soixante-dix-neuf
                    habilitations, c'est pres de dix-huit cents pastilles a parcourir
                    pour trouver la seule qui a bouge. Elle reste accessible d'un clic. */}
                {g.pliable.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={() => basculer(idx)}
                      style={{
                        border: "none",
                        background: "none",
                        padding: 0,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: 11,
                        color: "#6b7280",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      {deployes.has(idx) ? <DownOutlined /> : <RightOutlined />}
                      {g.pliable.length} {g.libellePliable}
                    </button>
                    {deployes.has(idx) && (
                      <Groupe titre="" habs={g.pliable} couleurTitre="#6b7280" />
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Commentaire */}
          {h.commentaire && (
            <>
              <Divider style={{ margin: "8px 0" }} />
              <Text
                type="secondary"
                style={{ fontSize: 12, fontStyle: "italic" }}
              >
                {t("historique.commentaire", { commentaire: h.commentaire })}
              </Text>
            </>
          )}
        </div>
      ),
    };
  };

  return (
    <CBModal
      title={
        <div>
          <Space>
            <ClockCircleOutlined style={{ color: "#1a6b3c" }} />
            <span>{t("historique.titre")}</span>
            {profil && (
              <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
                - {profil.profilNom}
              </Text>
            )}
          </Space>
          {/* La derniere modification, lisible sans parcourir la frise. */}
          {derniereMaj && (
            <Text
              type="secondary"
              style={{ display: "block", fontSize: 12, fontWeight: 400, marginTop: 2 }}
            >
              {t("historique.derniereMaj", {
                date: dayjs(derniereMaj).format("DD/MM/YYYY"),
                heure: dayjs(derniereMaj).format("HH:mm"),
              })}
            </Text>
          )}
        </div>
      }
      open={open}
      onCancel={onClose}
      width={760}
      footer={<Button onClick={onClose}>{t("historique.fermer")}</Button>}
      styles={{ body: { padding: "20px 24px" } }}
    >
      {loading ? (
        <div style={{ textAlign: "center", paddingTop: 60 }}>
          <Spin size="large" />
        </div>
      ) : items.length === 0 ? (
        <Empty
          description={t("historique.aucun")}
          style={{ paddingTop: 60 }}
        />
      ) : (
        <>
          <Badge
            count={total}
            style={{
              background: "#1a6b3c",
              marginBottom: 20,
              display: "block",
            }}
            overflowCount={999}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("historique.versionsEnregistrees", { count: total })}
            </Text>
          </Badge>

          <Timeline items={items.map(renderItem)} style={{ marginTop: 16 }} />

          {total > pageSize && (
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Pagination
                current={page + 1}
                pageSize={pageSize}
                total={total}
                onChange={handlePageChange}
                size="small"
                simple
              />
            </div>
          )}
        </>
      )}
    </CBModal>
  );
}
